// Lines of an earlier bill, picked for return or exchange. Selection and
// quantities go into Core's return state (checkedKeys, dataSource,
// handleSaveReturnCartRecord), and the main button hands over exactly as
// SuperMarket does: Core's ReturnOptionsModal when refund / credit note /
// exchange are offered (opened with the choice made here already ticked),
// otherwise a straight refund through addSelectedReturnProductsToCart.
// Reason, condition and refund-to are picked here and written onto the return
// cart once its lines land (utils/fashion/returnNote).
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import useTransactionStore from "../../../stores/useTransactionStore";
import useReturnPolicyStore from "../../../stores/useReturnPolicyStore";
import { formatMoney } from "../../../utils/fashion/money";
import useFinalSaleIds from "./hooks/useFinalSaleIds";
import { ONLINE_RETURN_BLOCKED, ONLINE_RETURN_NOTE, onlineReturnAllowed } from "../../../utils/fashion/onlineOrders";
import {
  billReturnDays,
  lineBlockedReason,
  lineMaxQty,
  parseReturnQty,
  returnChoices,
  returnPieces,
  returnTax,
  returnValue,
  withReturnQty,
} from "../../../utils/fashion/returnLines";
import { RETURN_CONDITIONS, clearReturnNote, refundTenders, returnNoteFields, returnReasonsOf, stampReturnNote } from "../../../utils/fashion/returnNote";
import "./FashionReturnView.css";

// "12 Oct 2026 12:48" from the bill's date and time, whichever are readable.
function billWhen(order) {
  const day = order?.dateordered && dayjs(order.dateordered).isValid() ? dayjs(order.dateordered).format("DD MMM YYYY") : "";
  const raw = String(order?.orderTime || "");
  const time = /^\d{1,2}:\d{2}/.test(raw) ? raw.slice(0, 5) : raw && dayjs(raw).isValid() ? dayjs(raw).format("HH:mm") : "";
  return [day, time].filter(Boolean).join(" ");
}

const unitGross = (r) => (r.linegross && r.qty ? Math.abs(r.linegross) / Math.abs(r.qty) : Number(r.grossstd) || 0);

export default function FashionReturnView({
  tillData,
  posConfig,
  selectedReturnOrder,
  dataSource,
  setDataSource,
  checkedKeys,
  setCheckedKeys,
  handleSaveReturnCartRecord,
  giftCardItems,
  setShowReturnOptions,
  addSelectedReturnProductsToCart,
  setIsPurchaseReturn,
  onlineReturn = false,
  onCancel,
}) {
  const rows = useMemo(() => (dataSource?.length ? dataSource : selectedReturnOrder?.line || []), [dataSource, selectedReturnOrder]);
  const keys = useMemo(() => (checkedKeys || []).filter((k) => k !== null && k !== undefined), [checkedKeys]);
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const finalSale = useFinalSaleIds(selectedReturnOrder, tillData);
  const refundBlocked = useReturnPolicyStore((st) => st.refundBlocked);
  const choices = useMemo(() => returnChoices(posConfig, onlineReturn || refundBlocked), [posConfig, onlineReturn, refundBlocked]);
  const [choice, setChoice] = useState(null);
  const settleAs = choices.find((c) => c.type === choice)?.type || choices[0]?.type || "REFUND";
  const reasons = useMemo(() => returnReasonsOf(tillData), [tillData]);
  const tenders = useMemo(() => refundTenders(selectedReturnOrder), [selectedReturnOrder]);
  const [reasonId, setReasonId] = useState(null);
  const [condition, setCondition] = useState(null);
  const [refundTo, setRefundTo] = useState(null);
  const [note, setNote] = useState("");
  const refundVia = refundTo || tenders.options[0];

  // Lines Core preselected that turn out to be final sale come off the selection.
  useEffect(() => {
    if (finalSale.ids.size === 0) return;
    const allowed = keys.filter((k) => !finalSale.ids.has(rows.find((r) => r.key === k)?.product?.mProductId));
    if (allowed.length !== keys.length) setCheckedKeys(allowed);
  }, [finalSale.ids]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new bill starts with clean drafts.
  useEffect(() => {
    setDrafts({});
    setErrors({});
    setMsg("");
    setReasonId(null);
    setCondition(null);
    setRefundTo(null);
    setNote("");
  }, [selectedReturnOrder?.documentno]);

  const toggle = (row) => {
    setMsg("");
    if (keys.includes(row.key)) {
      setCheckedKeys(keys.filter((k) => k !== row.key));
      return;
    }
    if (lineBlockedReason(row, giftCardItems, finalSale.ids)) return;
    setCheckedKeys([...keys, row.key]);
  };

  const commitQty = (row) => {
    const draft = drafts[row.key];
    if (draft === undefined) return;
    const { qty, error } = parseReturnQty(draft, row);
    if (error) {
      setErrors((e) => ({ ...e, [row.key]: error }));
      return;
    }
    setErrors((e) => ({ ...e, [row.key]: "" }));
    setDrafts((d) => {
      const next = { ...d };
      delete next[row.key];
      return next;
    });
    if (qty === Number(row.qtyToReturn)) return;
    const next = withReturnQty(rows, row.key, qty);
    setDataSource?.(next);
    handleSaveReturnCartRecord?.(next);
  };

  // Core's modal opens with this choice ticked; it still confirms, and its
  // Cancel clears the choice again. Only a type the modal offers is passed.
  const openOptions = (type) => {
    if (choices.some((c) => c.type === type)) useTransactionStore.getState().setSelectedReturnType(type);
    setShowReturnOptions?.(true);
  };

  const order = selectedReturnOrder || {};
  const pendingError = Object.values(errors).some(Boolean) || Object.keys(drafts).length > 0;

  const proceed = async (type = settleAs) => {
    if (busy) return;
    if (finalSale.status === "loading") {
      setMsg("Checking which items are final sale. Try again in a moment.");
      return;
    }
    // Lines Core selected after the final-sale check came back are dropped here.
    const allowed = keys.filter((k) => {
      const row = rows.find((r) => r.key === k);
      return row && !lineBlockedReason(row, giftCardItems, finalSale.ids);
    });
    if (allowed.length !== keys.length) {
      setCheckedKeys(allowed);
      setMsg("Some selected items can't be returned and were taken off. Check the list and try again.");
      return;
    }
    if (keys.length === 0) {
      setMsg("Pick at least one item to return.");
      return;
    }
    if (pendingError) {
      setMsg("Fix the highlighted quantity first.");
      return;
    }
    if (reasons.length > 0 && !reasonId) {
      setMsg("Pick why the pieces are coming back.");
      return;
    }
    const billIds = [order.sOrderID, ...rows.map((r) => r.sOrderId)];
    stampReturnNote(returnNoteFields({ reasonId, condition, refundTo: type === "REFUND" ? refundVia : null, note }), billIds);
    // Online orders were paid to the online store: the options modal is shown
    // without Refund (index.js), and with neither store credit nor exchange
    // switched on there is nothing the till can give back.
    if (onlineReturn) {
      if (!onlineReturnAllowed(posConfig)) {
        setMsg(ONLINE_RETURN_BLOCKED);
        return;
      }
      setMsg("");
      openOptions(type);
      return;
    }
    setMsg("");
    if (posConfig?.allowReturnRefund === "Y" || posConfig?.allowReturnCreditNote === "Y" || posConfig?.allowReturnExchange === "Y") {
      openOptions(type);
      return;
    }
    setBusy(true);
    try {
      useTransactionStore.getState().setSelectedReturnType("REFUND");
      const result = await addSelectedReturnProductsToCart?.(keys);
      if (result) setIsPurchaseReturn?.(false);
    } finally {
      setBusy(false);
    }
  };

  const total = returnValue(rows, keys);
  const pieces = returnPieces(rows, keys);
  const tax = returnTax(rows, keys);
  const days = billReturnDays(rows);
  const customer = order.cwrB2cCustomer;
  const who = [customer?.name, customer?.mobileNo].filter(Boolean).join(", ");
  const meta = [billWhen(order), order.cwrTill?.till && `Till ${order.cwrTill.till}`, who].filter(Boolean);
  const hasFinalSale = rows.some((r) => finalSale.ids.has(r.product?.mProductId));
  const pickedRows = rows.filter((r) => keys.includes(r.key) && Number(r.qtyToReturn) > 0);
  const canExchange = choices.some((c) => c.type === "EXCHANGE");
  const primary =
    settleAs === "STORE_CREDIT"
      ? `Credit note ${formatMoney(total, tillData)}`
      : settleAs === "EXCHANGE"
        ? "Exchange"
        : `Refund ${formatMoney(-total, tillData)}`;

  return (
    <div className="fp-return fp-ret-split">
      <div className="fp-ret-page">
        <p className="fp-ret-head" role="status">
          <span className="fp-ret-lbl">Bill</span> <span className="fp-num fp-ret-billno">{order.documentno}</span>
          {meta.length > 0 && <span className="fp-muted"> · {meta.join(" · ")}</span>}
        </p>
        {onlineReturn && (
          <p className="fp-line-msg is-warn" role="status">
            {ONLINE_RETURN_NOTE}
          </p>
        )}
        {finalSale.status === "error" && (
          <p className="fp-line-msg is-warn" role="status">
            Couldn't check this bill for final-sale items. Check the tags before taking anything back.
          </p>
        )}
        <section className="fp-ret-card" aria-labelledby="fp-ret-what">
          <h3 id="fp-ret-what" className="fp-ret-h">
            What is coming back?
          </h3>
          <p className="fp-muted fp-ret-hint">
            Tick each piece the customer brought{days ? `. The return window is ${days} days.` : "."}
          </p>
          <ul className="fp-panel-list">
            {rows.length === 0 && <li className="fp-empty">This bill has no lines to return.</li>}
            {rows.map((row) => {
              const blocked = lineBlockedReason(row, giftCardItems, finalSale.ids);
              const isFinal = finalSale.ids.has(row.product?.mProductId);
              const on = keys.includes(row.key);
              const max = lineMaxQty(row);
              const id = `fp-ret-${row.key}`;
              const err = errors[row.key];
              const sku = row.product?.value;
              const offer = row.pricingRule?.name;
              return (
                <li key={row.key} className={`fp-panel-row fp-ret-row${on ? " is-on" : ""}${blocked ? " is-off" : ""}`}>
                  <input id={id} type="checkbox" className="fp-ret-check" checked={on} disabled={Boolean(blocked) && !on} onChange={() => toggle(row)} />
                  <label className="fp-panel-main" htmlFor={id}>
                    <span className="fp-line-name">{row.product?.name || "Item"}</span>
                    <span className="fp-muted">
                      {[sku, `paid ${formatMoney(unitGross(row), tillData)} each`, offer].filter(Boolean).join(" · ")}
                    </span>
                    <span className="fp-muted">{blocked || `${max} of ${Number(row.qty)} can come back`}</span>
                  </label>
                  <span className={`fp-pill ${blocked ? "p-neu" : "p-ok"}`}>{isFinal ? "Final sale" : blocked ? "Can't return" : "Can return"}</span>
                  {row.product?.returnable && !blocked ? (
                    <span className="fp-ret-qty">
                      <input
                        className="fp-qty-input fp-num"
                        inputMode="decimal"
                        aria-label={`Quantity to return, ${row.product?.name || "item"}`}
                        aria-invalid={err ? "true" : "false"}
                        value={drafts[row.key] ?? String(row.qtyToReturn ?? "")}
                        onChange={(e) => setDrafts((d) => ({ ...d, [row.key]: e.target.value }))}
                        onBlur={() => commitQty(row)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitQty(row);
                          }
                        }}
                      />
                      {err && (
                        <span className="fp-line-msg is-err" role="alert">
                          {err}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="fp-num fp-ret-qty">{blocked ? "–" : Number(row.qtyToReturn)}</span>
                  )}
                  <span className="fp-num fp-ret-amt">{blocked ? "" : formatMoney(unitGross(row) * Number(row.qtyToReturn || 0), tillData)}</span>
                </li>
              );
            })}
          </ul>
          {hasFinalSale && <p className="fp-ret-warn">Final-sale pieces can't come back on this bill.</p>}
        </section>
      </div>
      <aside className="fp-ret-side" aria-label="Refund">
        <h3 className="fp-ret-h">
          {pieces === 1 ? "1 piece" : `${pieces} pieces`} coming back
        </h3>
        {choices.length > 0 && (
          <div className="fp-ret-field">
            <span className="fp-ret-lbl">Give the customer</span>
            <div className="fp-ret-seg" role="group" aria-label="Settle as">
              {choices.map((c) => (
                <button key={c.type} type="button" className={settleAs === c.type ? "is-on" : ""} aria-pressed={settleAs === c.type} onClick={() => setChoice(c.type)}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {reasons.length > 0 && (
          <div className="fp-ret-field">
            <span className="fp-ret-lbl" id="fp-ret-why">
              Why is it coming back?
            </span>
            <div className="fp-ret-chips" role="group" aria-labelledby="fp-ret-why">
              {reasons.map((r) => (
                <button
                  key={r.returnReasonId}
                  type="button"
                  className={reasonId === r.returnReasonId ? "is-on" : ""}
                  aria-pressed={reasonId === r.returnReasonId}
                  onClick={() => {
                    setReasonId(r.returnReasonId);
                    setMsg("");
                  }}
                >
                  {r.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="fp-ret-field">
          <span className="fp-ret-lbl" id="fp-ret-cond">
            Condition
          </span>
          <div className="fp-ret-chips" role="group" aria-labelledby="fp-ret-cond">
            {RETURN_CONDITIONS.map((c) => (
              <button key={c.value} type="button" className={condition === c.value ? "is-on" : ""} aria-pressed={condition === c.value} onClick={() => setCondition(condition === c.value ? null : c.value)}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        {settleAs === "REFUND" && (
          <div className="fp-ret-field">
            <span className="fp-ret-lbl" id="fp-ret-to">
              Refund to
            </span>
            <div className="fp-ret-chips" role="group" aria-labelledby="fp-ret-to">
              {tenders.options.map((t) => (
                <button key={t} type="button" className={refundVia === t ? "is-on" : ""} aria-pressed={refundVia === t} onClick={() => setRefundTo(t)}>
                  {t}
                </button>
              ))}
            </div>
            <span className="fp-muted">
              {tenders.paidBy ? `Paid by ${tenders.paidBy}. ` : ""}Choose {refundVia} on the pay screen.
            </span>
          </div>
        )}
        <input className="fp-ret-note" aria-label="Return note" placeholder="Note (optional)" value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} />
        <div className="fp-ret-sum">
          {pickedRows.map((r) => (
            <div key={r.key} className="fp-ret-sum-row">
              <span>
                {r.product?.name || "Item"}
                {Number(r.qtyToReturn) !== 1 ? ` × ${Number(r.qtyToReturn)}` : ""}
              </span>
              <span className="fp-num">{formatMoney(unitGross(r) * Number(r.qtyToReturn), tillData)}</span>
            </div>
          ))}
          {tax.amount > 0 && (
            <div className="fp-ret-sum-row fp-muted">
              <span>Includes GST{tax.rate ? ` ${tax.rate}%` : ""}</span>
              <span className="fp-num">{formatMoney(tax.amount, tillData)}</span>
            </div>
          )}
          <div className="fp-ret-sum-row fp-ret-total">
            <span>{keys.length} picked</span>
            <strong className="fp-num">{formatMoney(total, tillData)}</strong>
          </div>
        </div>
        {msg && (
          <p className="fp-line-msg is-err" role="alert">
            {msg}
          </p>
        )}
        <div className="fp-ret-acts">
          <button
            type="button"
            className="fp-btn-ghost fp-btn-sm"
            onClick={() => {
              clearReturnNote();
              onCancel?.();
            }}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="button" className="fp-btn-pay fp-btn-sm" onClick={() => proceed()} disabled={busy || keys.length === 0}>
            {busy ? "Adding…" : primary}
          </button>
        </div>
        {canExchange && settleAs !== "EXCHANGE" && (
          <button type="button" className="fp-btn-ghost fp-btn-sm fp-ret-alt" onClick={() => proceed("EXCHANGE")} disabled={busy || keys.length === 0}>
            Exchange instead
          </button>
        )}
      </aside>
    </div>
  );
}
